import { hasRoles, type HasRolesOptions } from '../../permissions/api/hasRoles.js';
import { INTERNAL_ROLES, ROLE_LIFECYCLES } from '../../permissions/roles.js';

/**
 * What a person's state in our system does to their account in Entra.
 *
 * A person is in one phase of each lifecycle per school (`ROLE_LIFECYCLES`). For the account the furthest-along phase
 * across all of them counts - an Ehemaliger Mitarbeiter who is a Sorgeberechtigter keeps it:
 *
 * | state                                                    | account                  | groups                     | license                         |
 * | -------------------------------------------------------- | ------------------------ | -------------------------- | ------------------------------- |
 * | Bewerbung: Kandidat, SuS-Bewerber, SuS-Bewerber SB       | created, enabled         | the active bookings        | none                            |
 * | Aktiv: Mitarbeiter, Schüler, Sorgeberechtigter, Spender; | created, enabled         | the active bookings        | Mitarbeiter: their contract     |
 * | or an internal role (Admin, PSB, ...)                    |                          |                            | booking, see `LICENSE_GROUP_ID` |
 * | only Ehemalig or abgelehnt                               | disabled                 | they end with the bookings | ends with the contract          |
 * | no lifecycle at all                                      | none created, disabled   | the active bookings        | none                            |
 * | deleted                                                  | disabled                 | left, all we manage        | gone with the groups            |
 *
 * Groups and licenses are bookings, not roles: an account is in exactly the groups its active bookings name, and the
 * license is one of those groups. A booking left open after somebody left keeps them in that group - disabled.
 *
 * Disabled is not deleted. The account keeps its login and its addresses, and the next sync enables it again once the
 * person is back in a phase above - a re-hired employee, a parent whose next child applies.
 */
const ENABLING_ROLES: string[] = [
  ...ROLE_LIFECYCLES.flatMap(({ stages, active }) => stages.slice(0, stages.indexOf(active) + 1)),
  ...INTERNAL_ROLES,
];

/** Whether the person's account in Entra is supposed to be enabled - and to be created, when there is none. */
export const isAccountEnabledInEntra = (user: HasRolesOptions['user']): boolean =>
  hasRoles({ user, roles: ENABLING_ROLES });
